import dvaModelExtend from 'dva-model-extend';
import _ from 'lodash';

import { bindServices } from '@/services/api';
import { createCrudModel } from '@/utils/core/dva';
import CoreQueries from '@/utils/input/queries';

const namespace = 'aiVectors';
const fetchRoute = 'ai/vector';
const fetchParams = 'AiVector';
const api = bindServices('aiVectors');

// File management lives under the nested route `ai/vector/{vectorId}/file`.
// The catalog CRUD (create/update of the vector itself) must NEVER carry the
// `files` collection — see the AI Vectors UX guide.
const fileRoute = (vectorId) => `${fetchRoute}/${vectorId}/file`;

const _model = createCrudModel({
  namespace,
  fetchRoute,
  fetchParams,
  entityName: 'aiVector',
  api,
  features: { import: false, bulkActions: false, listCache: true },
  state: {
    // Nested AIVectorFile state for the config drawer.
    files: [],
    filesLoading: false,
    filesVectorId: null,
    // Separate "Configurar" drawer (file management) — kept distinct from the
    // metadata `showForm` drawer so Edit and Configure are two different flows.
    showConfig: false,
    configId: null,
  },
  effects: {
    *fetchCreate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        const res = yield call(api.create, {
          route: fetchRoute,
          data: payload.data,
        });
        yield put({ type: 'clearListCache' });

        // Surface the freshly created vector in `list` so the EntitySelector's
        // inline-create flow can auto-select it (same trick as `aiSkills`):
        // the picker reads `state.aiVectors.list` right after `success` flips
        // to true and selects the first item that isn't already chosen.
        const created = res?.data || res || {};
        const createdItem = { ...payload.data, ...created };
        if (createdItem.id) {
          yield put({ type: 'prependCreated', payload: createdItem });
        }

        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0].field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors || [],
          });
          yield put({
            type: 'errors',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },

    // ── Configurar drawer (file management) ────────────────────────────────
    *showConfig({ payload }, { put }) {
      yield put({ type: 'querySetConfig', payload });
    },

    // ── AIVectorFile (nested) ──────────────────────────────────────────────
    *fetchFiles({ payload }, { call, put }) {
      const { vectorId } = payload;
      if (!vectorId) return;
      try {
        yield put({ type: 'setFilesLoading', payload: { loading: true, vectorId } });
        const res = yield call(api.getAll, {
          route: `${fileRoute(vectorId)}/paged`,
          params: { ...CoreQueries.AiVector.file, page: 1, limit: 200 },
          readPriority: true,
        });
        const items = Array.isArray(res?.items) ? res.items : Array.isArray(res) ? res : [];
        yield put({ type: 'setFiles', payload: { files: items, vectorId } });
      } catch (e) {
        yield put({ type: 'setFiles', payload: { files: [], vectorId } });
      } finally {
        yield put({ type: 'setFilesLoading', payload: { loading: false } });
      }
    },

    *createFile({ payload }, { call, put }) {
      const { vectorId, data } = payload;
      if (!vectorId) return;
      try {
        yield call(api.create, { route: fileRoute(vectorId), data });
        yield put({ type: 'fetchFiles', payload: { vectorId } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      }
    },

    *activateFile({ payload }, { call, put }) {
      const { vectorId, id, state } = payload;
      if (!vectorId || !id) return;
      try {
        yield call(api.activate, { route: fileRoute(vectorId), id, isActive: state });
        yield put({ type: 'fetchFiles', payload: { vectorId } });
      } catch (e) {
        /* surfaced via notifications elsewhere */
      }
    },

    *deleteFile({ payload }, { call, put }) {
      const { vectorId, id } = payload;
      if (!vectorId || !id) return;
      try {
        yield call(api.remove, { route: fileRoute(vectorId), id });
        yield put({ type: 'fetchFiles', payload: { vectorId } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      }
    },

    // Retry OpenAI sync for a single failed/not-synced file.
    // `POST ai/vector/{vectorId}/file/{fileId}/retry-sync`.
    *retrySyncFile({ payload }, { call, put }) {
      const { vectorId, id } = payload;
      if (!vectorId || !id) return;
      try {
        yield call(api.apiRoute, {
          route: `${fileRoute(vectorId)}/${id}/retry-sync`,
          method: 'post',
        });
        yield put({ type: 'fetchFiles', payload: { vectorId } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      }
    },

    // Retry OpenAI sync for every pending/failed active file of the vector.
    // `POST ai/vector/{vectorId}/file/retry-sync`.
    *retrySyncAllFiles({ payload }, { call, put }) {
      const { vectorId } = payload;
      if (!vectorId) return;
      try {
        yield call(api.apiRoute, {
          route: `${fileRoute(vectorId)}/retry-sync`,
          method: 'post',
        });
        yield put({ type: 'fetchFiles', payload: { vectorId } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      }
    },
  },
  reducers: {
    querySetConfig(state, { payload }) {
      return {
        ...state,
        showConfig: !!payload.state,
        configId: payload.state ? payload.id : null,
      };
    },

    prependCreated(state, { payload }) {
      const created = {
        ...payload,
        isActive: payload.isActive !== false,
      };
      const withoutDup = state.list.filter((item) => item.id !== created.id);
      return { ...state, list: [created, ...withoutDup] };
    },

    setFilesLoading(state, { payload }) {
      return {
        ...state,
        filesLoading: !!payload.loading,
        ...(payload.vectorId ? { filesVectorId: payload.vectorId } : {}),
      };
    },

    setFiles(state, { payload }) {
      return { ...state, files: payload.files || [], filesVectorId: payload.vectorId ?? null };
    },

    clearFiles(state) {
      return { ...state, files: [], filesLoading: false, filesVectorId: null };
    },
  },
});

export default dvaModelExtend(_model, { namespace });
